/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../../common/constants';

const feedbacks = types.model('feedbacks', {
  'Answer Relevance': types.maybeNull(types.number),
  reason: types.maybeNull(types.string),
  'Context Relevance': types.maybeNull(types.number),
  Groundedness: types.maybeNull(types.number),
  Correctness: types.maybeNull(types.number),
  statement: types.maybeNull(types.string),
  reasons: types.maybeNull(types.string),
  gpt_groundedness: types.maybeNull(types.number),
  gpt_relevance: types.maybeNull(types.number),
  gpt_coherence: types.maybeNull(types.number),
  gpt_fluency: types.maybeNull(types.number),
  gpt_similarity: types.maybeNull(types.number),
  f1_score: types.maybeNull(types.number),
  gpt_retrieval: types.maybeNull(types.number)
});

const metadatas = types.model('metadata', {
  executionId: types.maybeNull(types.string),
  dateExecuted: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.string),
  executedBy: types.maybeNull(types.string),
  count: types.maybeNull(types.number),
  s3FilePath: types.maybeNull(types.string),
  testName: types.maybeNull(types.string),
  evalName: types.maybeNull(types.string),
  graphVersion: types.maybeNull(types.number),
  status: types.maybeNull(types.string),
  testId: types.maybeNull(types.string)
});

const record = types.model('metadata', {
  input: types.maybeNull(types.string),
  output: types.maybeNull(types.string),
  prompt: types.maybeNull(types.string),
  requestId: types.maybeNull(types.string),
  feedbacks: types.optional(types.array(feedbacks), [])
});

const executionResult = types.model('executionResult', {
  executionId: types.string,
  scores: types.optional(types.array(record), [])
});

const effectiveness = types.model('effectiveness', {
  metadata: types.optional(types.maybeNull(metadatas))
});

const EffectivenessData = types
  .model('EffectivenessData', {
    data: types.optional(types.array(effectiveness), []),
    executionResults: types.optional(types.array(executionResult), []),
    fileName: types.maybeNull(types.string)
  })
  .actions((self) => ({
    fetch(payload) {
      Framework.getEventManager().publish(constants.GET_STUDYTOOL_EFFECTIVENESS_DATA, {
        payload
      });
    },
    fetchMetadata(tenantId) {
      Framework.getEventManager().publish(constants.GET_STUDYTOOL_EFFECTIVENESS_METADATA, { tenantId });
    },
    setMetaDataResponse(response) {
      const { data } = response;
      applySnapshot(self, { ...getSnapshot(self), data });
      Framework.getEventManager().publish(constants.SET_STUDYTOOL_EFFECTIVENESS_METADATA);
    },
    initiate(payload) {
      Framework.getEventManager().publish(constants.EFFECTIVENESS_INITIATE_DATA, { payload });
    },
    initiateFileUpload(payload, tenantId) {
      Framework.getEventManager().publish(constants.EFFECTIVENESS_TRIGGER_FILE_UPLOAD, { payload, tenantId });
    },
    fetchLLMResults(payload) {
      Framework.getEventManager().publish(constants.GET_LLM_TEST_RESULTS_DATA, {
        payload
      });
    },
    fetchLLMResultsByExecutionId(payload) {
      Framework.getEventManager().publish(constants.GET_LLM_RESULTS_BY_EXECUTION_ID_DATA, {
        payload
      });
    },
    setResponse(response) {
      const { data } = response;
      applySnapshot(self, { ...getSnapshot(self), data });
      Framework.getEventManager().publish(constants.SET_STUDYTOOL_EFFECTIVENESS_DATA);
    },
    setExecutionResults(response, executionIdFromRequest) {
      const { data } = response;
      self.executionResults.push({ executionId: executionIdFromRequest, scores: data[0].scores });
      Framework.getEventManager().publish(constants.SET_STUDYTOOL_EFFECTIVENESS_EXECUTION_RESULTS);
    },
    setFileUploadResponse(response) {
      const { data } = response;
      applySnapshot(self, { ...getSnapshot(self), fileName: data.fileName });
      Framework.getEventManager().publish(constants.EFFECTIVENESS_SET_FILE_UPLOAD_RESPONSE);
    },
    setFileEmpty() {
      applySnapshot(self, { ...getSnapshot(self), fileName: '' });
      Framework.getEventManager().publish(constants.EFFECTIVENESS_SET_FILE_UPLOAD_RESPONSE);
    },
    resetStoreValues() {
      const initialState = [];
      applySnapshot(self.data, initialState);
      Framework.getEventManager().publish(constants.SET_STUDYTOOL_EFFECTIVENESS_DATA);
    }
  }));

export default EffectivenessData;
